// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useBrowserDetection } from "@/hooks/useBrowserDetection";
import { z } from "zod";
import { Loader2, MailCheck } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import { Separator } from "@/components/ui/separator";
import { logAuthEvent } from "@/services/authLogger";
import { Skeleton } from "@/components/ui/skeleton";
import { lovable } from "@/integrations/lovable/index";
import { isProductionApp } from "@/lib/appHost";
import BrandLogo from "@/components/BrandLogo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BetaWaitlistForm from "@/components/auth/BetaWaitlistForm";
import { BETA_FULL_MESSAGE, fetchBetaSignupStatus } from "@/services/admin/betaSignup";

/** Défaut uniquement si security_settings ne fournit pas la valeur. */
const DEFAULT_PASSWORD_MIN_LENGTH = 8;

const normalizeEmail = (value: string) => value.trim().toLowerCase();

// Schéma construit avec la longueur minimale lue dans les réglages admin.
const buildAuthSchema = (minLength: number) =>
  z.object({
    email: z.string().email({ message: "Email invalide" }),
    password: z.string().min(minLength, {
      message: `Le mot de passe doit contenir au moins ${minLength} caractères`,
    }),
    fullName: z.string().optional(),
  });

/** Clé sessionStorage mémorisant la destination pendant un aller-retour OAuth. */
const AUTH_NEXT_KEY = "tama_auth_next";

interface AuthPanelProps {
  /** Affiché dans une popup (feed public) : pas de mise en page plein écran. */
  embedded?: boolean;
  /** Appelé lorsque la session est établie (au lieu d'une redirection). */
  onDone?: () => void;
}

/**
 * Formulaire d'authentification (connexion, inscription, mot de passe oublié,
 * réinitialisation). Utilisable en page autonome ou dans la popup du feed.
 */
const AuthPanel = ({ embedded = false, onDone }: AuthPanelProps) => {
  const [searchParams] = useSearchParams();
  const isResetting = searchParams.get('reset') === 'true';

  // Preserve a redirect target through sign-in / sign-up / OAuth (required by
  // MCP OAuth consent flow at /.lovable/oauth/consent).
  const rawNext = searchParams.get('next') ?? '/';
  const nextPath = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
  const oauthRedirectUrl = `${window.location.origin}${nextPath}`;


  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [fullName, setFullName] = useState("");

  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [oauthProvider, setOauthProvider] = useState<'google' | 'apple' | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: securitySettings, isLoading: isLoadingSettings } = useSecuritySettings();
  const configuredMin = Number(securitySettings?.auth_security?.password_min_length);
  const passwordMinLength = Number.isFinite(configuredMin) && configuredMin > 0 ? configuredMin : DEFAULT_PASSWORD_MIN_LENGTH;
  
  /**
   * Le reCAPTCHA n'est actif que sur l'hôte de production : la clé Google
   * est liée à ce domaine et refuse les previews Lovable / localhost.
   */
  const recaptchaSiteKey = securitySettings?.auth_security?.recaptcha_site_key || import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const isRecaptchaEnabled =
    isProductionApp() &&
    securitySettings?.auth_security?.recaptcha_enabled &&
    recaptchaSiteKey &&
    recaptchaSiteKey.trim() !== "";

  
  // Les identifiants/secrets OAuth ne sont jamais exposés au navigateur :
  // seul l'indicateur d'activation est publié par la base.
  const isGoogleOAuthEnabled = !!securitySettings?.auth_security?.google_oauth_enabled;

  const isAppleOAuthEnabled = !!securitySettings?.auth_security?.apple_oauth_enabled;

  // Le bouton Apple n'a de sens que sur un appareil Apple (iOS, iPadOS, macOS).
  const { isAppleDevice } = useBrowserDetection();

  // Palier bêta plein : connexion seule + liste d'attente.
  const { data: betaStatus } = useQuery({ queryKey: ["beta-signup-status"], queryFn: fetchBetaSignupStatus, staleTime: 30_000 });
  const betaClosed = !!betaStatus?.enabled && !betaStatus.open;
  useEffect(() => {
    if (betaClosed) setIsLogin(true);
  }, [betaClosed]);

  const showBetaTabs = betaClosed && !isForgotPassword && !isResetting;
  const [betaTab, setBetaTab] = useState<"waitlist" | "login">("waitlist");
  const showOAuthButtons = (isGoogleOAuthEnabled || (isAppleOAuthEnabled && isAppleDevice)) && !betaClosed;

  /**
   * Résout la destination après authentification : on privilégie la valeur
   * mémorisée avant un départ OAuth (le fournisseur nous renvoie sur /auth nu),
   * sinon le paramètre `next` de l'URL. Toujours un chemin same-origin.
   */
  const resolveNextPath = () => {
    const stored = sessionStorage.getItem(AUTH_NEXT_KEY);
    sessionStorage.removeItem(AUTH_NEXT_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) return stored;
    return nextPath;
  };

  /**
   * Fin du parcours d'authentification : en popup, on rend la main au feed
   * (l'assistant d'onboarding prend le relais) ; en page, on redirige.
   */
  const finishAuth = () => {
    if (embedded) {
      sessionStorage.removeItem(AUTH_NEXT_KEY);
      // Après un reset, on nettoie l'URL (query + jeton dans le hash) pour
      // déverrouiller le voile d'AuthGate.
      if (isResetting) navigate("/", { replace: true });
      onDone?.();
      return;
    }
    navigate(resolveNextPath());
  };

  // Session déjà active (retour OAuth notamment) : on clôt immédiatement,
  // SAUF pendant une réinitialisation où la session recovery est attendue.
  useEffect(() => {
    if (isResetting) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        finishAuth();
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) finishAuth();
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);




  /**
   * Connexion / création de compte via un fournisseur social géré (Google, Apple).
   * `redirect_uri` = origine publique du site (jamais une route protégée ni un
   * chemin spécifique) afin de correspondre exactement à l'URL de retour
   * autorisée côté fournisseur ; la destination réelle est mémorisée en
   * sessionStorage et appliquée une fois la session confirmée.
   */
  const handleOAuthSignIn = async (provider: 'google' | 'apple') => {
    setOauthProvider(provider);
    try {
      sessionStorage.setItem(AUTH_NEXT_KEY, nextPath);
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });


      if (result.error) {
        throw new Error("La connexion via ce service a échoué. Merci de réessayer.");
      }

      if (result.redirected) {
        // Le navigateur part vers le fournisseur : rien de plus à faire ici.
        return;
      }

      // Session déjà établie (flux popup) : la redirection est gérée par onAuthStateChange.
    } catch (error) {
      sessionStorage.removeItem(AUTH_NEXT_KEY);
      setOauthProvider(null);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error instanceof Error ? error.message : "Connexion impossible.",
      });
    }
  };


  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('check-rate-limit', {
        body: {
          operationType: 'password_reset',
          email: normalizeEmail(email)
        }
      });

      if (rateLimitError || !rateLimitData?.allowed) {
        const remainingAttempts = rateLimitData?.remainingAttempts ?? 0;
        throw new Error(
          `Trop de tentatives de réinitialisation. ${remainingAttempts > 0 ? `Il vous reste ${remainingAttempts} tentative(s).` : 'Veuillez réessayer dans 24h.'}`
        );
      }

      if (isRecaptchaEnabled && !recaptchaToken) {
        throw new Error("Veuillez valider le reCAPTCHA");
      }

      if (isRecaptchaEnabled && recaptchaToken) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('validate-recaptcha', {
          body: { token: recaptchaToken }
        });

        if (verifyError || !verifyData?.valid) {
          throw new Error("Validation reCAPTCHA échouée. Veuillez réessayer.");
        }
      }

      const { error } = await supabase.auth.resetPasswordForEmail(normalizeEmail(email), {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) throw error;

      toast({
        title: "Email envoyé !",
        description: "Vérifie ta boîte mail pour réinitialiser ton mot de passe. Pense à vérifier tes courriers indésirables.",
      });
      
      setIsForgotPassword(false);
      setEmail("");
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } catch (error) {
      if (error instanceof Error) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword.length < passwordMinLength) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: `Le mot de passe doit contenir au moins ${passwordMinLength} caractères`,
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
      });
      return;
    }

    setLoading(true);

    try {
      if (securitySettings?.auth_security?.leaked_password_protection_enabled) {
        const { data: leakCheckData, error: leakCheckError } = await supabase.functions.invoke('check-leaked-password', {
          body: { password: newPassword }
        });

        if (leakCheckError) {
          console.error('Error checking password:', leakCheckError);
        }

        if (leakCheckData?.compromised) {
          toast({
            variant: "destructive",
            title: "Mot de passe compromis",
            description: `Ce mot de passe a été exposé ${leakCheckData.count?.toLocaleString()} fois dans des fuites de données. Choisissez un mot de passe plus sûr.`,
          });
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Mot de passe mis à jour !",
        description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe",
      });

      finishAuth();
    } catch (error) {
      if (error instanceof Error) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Renvoie l'e-mail de confirmation d'inscription (limite de débit Auth respectée).
  const handleResendConfirmation = async () => {
    const target = normalizeEmail(email);
    if (!z.string().email().safeParse(target).success) {
      toast({ variant: "destructive", title: "Erreur", description: "Saisis d'abord ton adresse e-mail." });
      return;
    }
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: target,
        options: { emailRedirectTo: `${window.location.origin}/` },
      });
      if (error) {
        console.error("Resend confirmation failed:", error.status);
        const tooMany = error.status === 429 || /rate limit|seconds/i.test(error.message);
        toast({
          variant: "destructive",
          title: "Envoi impossible",
          description: tooMany
            ? "Trop de demandes. Réessaie dans quelques minutes."
            : "L'e-mail n'a pas pu être renvoyé. Réessaie plus tard.",
        });
        return;
      }
      setConfirmationSent(true);
      toast({ title: "E-mail renvoyé", description: "Vérifie ta boîte de réception et tes courriers indésirables." });
    } finally {
      setResending(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowResend(false);

    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase.functions.invoke('check-rate-limit', {
        body: {
          operationType: isLogin ? 'login' : 'signup',
          email: normalizeEmail(email)
        }
      });

      if (rateLimitError || !rateLimitData?.allowed) {
        const remainingAttempts = rateLimitData?.remainingAttempts ?? 0;
        throw new Error(
          `Trop de tentatives. ${remainingAttempts > 0 ? `Il vous reste ${remainingAttempts} tentative(s).` : 'Veuillez réessayer plus tard.'}`
        );
      }

      if (isRecaptchaEnabled && !recaptchaToken) {
        throw new Error("Veuillez valider le reCAPTCHA");
      }

      if (isRecaptchaEnabled && recaptchaToken) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('validate-recaptcha', {
          body: { token: recaptchaToken }
        });

        if (verifyError || !verifyData?.valid) {
          throw new Error("Validation reCAPTCHA échouée. Veuillez réessayer.");
        }
      }

      const validatedData = buildAuthSchema(passwordMinLength).parse({
        email: normalizeEmail(email),
        password,
        fullName: isLogin ? undefined : fullName.trim(),
      });





      if (!isLogin && securitySettings?.auth_security?.leaked_password_protection_enabled) {
        const { data: leakCheckData, error: leakCheckError } = await supabase.functions.invoke('check-leaked-password', {
          body: { password: validatedData.password }
        });

        if (leakCheckError) {
          console.error('Error checking password:', leakCheckError);
        }

        if (leakCheckData?.compromised) {
          throw new Error(
            `Ce mot de passe a été exposé ${leakCheckData.count} fois dans des fuites de données. Veuillez en choisir un autre pour votre sécurité.`
          );
        }
      }

      if (isLogin) {
        const { data: loginData, error } = await supabase.auth.signInWithPassword({
          email: validatedData.email,
          password: validatedData.password,
        });

        if (error) {
          // Log échec de connexion
          await logAuthEvent({
            action: 'login_failed',
            email: validatedData.email,
            metadata: { error: error.message }
          });
          
          if (error.message.includes("Invalid login credentials")) {
            throw new Error("Email ou mot de passe incorrect");
          }
          if (error.message.includes("Email not confirmed")) {
            setShowResend(true);
            throw new Error("Veuillez vérifier votre email et cliquer sur le lien de confirmation avant de vous connecter.");
          }
          throw error;
        }

        // Log connexion réussie
        await logAuthEvent({
          action: 'login',
          userId: loginData.user?.id,
          email: loginData.user?.email,
          fullName: loginData.user?.user_metadata?.full_name
        });

        toast({
          title: "Bienvenue sur Tama !",
          description: "Connexion réussie",
        });
        finishAuth();

      } else {
        const { data: signUpData, error } = await supabase.auth.signUp({
          email: validatedData.email,
          password: validatedData.password,
          options: {
            data: {
              full_name: validatedData.fullName,
            },
            emailRedirectTo: `${window.location.origin}/`,
          },
        });

        if (error) {
          if (error.message.includes("User already registered")) {
            setShowResend(true);
            throw new Error("Cet email est déjà utilisé");
          }
          // Refus du palier bêta côté serveur (création du compte annulée).
          if (/bêta|Database error saving new user/i.test(error.message)) {
            throw new Error(BETA_FULL_MESSAGE);
          }
          throw error;
        }

        // Adresse déjà liée à un compte : Auth renvoie un user sans session et sans identité.
        const identities = signUpData.user?.identities;
        if (signUpData.user && !signUpData.session && Array.isArray(identities) && identities.length === 0) {
          setShowResend(true);
          throw new Error("Cet email est déjà utilisé");
        }



        // Log inscription
        await logAuthEvent({
          action: 'signup',
          userId: signUpData.user?.id,
          email: validatedData.email,
          fullName: validatedData.fullName
        });

        // Vérifier si l'email doit être confirmé
        if (signUpData.user && !signUpData.session) {
          toast({
            title: "Compte créé !",
            description: "Un e-mail de confirmation a été envoyé. Ouvre-le pour activer ton compte. Pense à vérifier tes courriers indésirables.",
            duration: 10000,
          });
          setConfirmationSent(true);
          setShowResend(false);
          setIsLogin(true);
          setPassword("");
          setFullName("");
        } else {
          // Envoyer l'email de bienvenue
          try {
            await supabase.functions.invoke('send-welcome-email', {
              body: {
                email: validatedData.email,
                fullName: validatedData.fullName || ""
              }
            });
          } catch (emailError) {
            // Ne pas bloquer l'inscription si l'email échoue
            console.error("Failed to send welcome email:", emailError);
          }

          toast({
            title: "Compte créé !",
            description: "Bienvenue sur Tama",
          });
          // La déclaration d'âge et la finalisation du profil sont demandées
          // juste après par l'assistant d'onboarding.
          finishAuth();
        }


      }
      
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Erreur de validation",
          description: error.errors[0].message,
        });
      } else if (error instanceof Error) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Formulaire de connexion / mot de passe oublié / inscription (réutilisé dans l'onglet bêta).
  const loginForm = (
    <>
              {confirmationSent && isLogin && !isForgotPassword && (
                <Alert className="mb-3">
                  <MailCheck className="h-4 w-4" />
                  <AlertDescription>
                    Un e-mail de confirmation a été envoyé. Ouvre-le pour activer ton compte. Pense à vérifier tes courriers indésirables.
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={isForgotPassword ? handleResetPassword : handleAuth} className="space-y-3">
                {!isLogin && !isForgotPassword && (
                  <div className="space-y-1.5">
                    <Label htmlFor="fullName" className="text-sm">Nom complet <span className="text-destructive">*</span></Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Votre nom"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                )}




                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm">Email <span className="text-destructive">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                {!isForgotPassword && (
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm">Mot de passe <span className="text-destructive">*</span></Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                )}

                {isRecaptchaEnabled && (
                  <div className="flex justify-center scale-90 origin-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      onChange={(token) => setRecaptchaToken(token)}
                    />
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isForgotPassword ? "Envoi..." : isLogin ? "Connexion..." : "Création du compte..."}
                    </>
                  ) : isForgotPassword ? (
                    "Envoyer le lien de réinitialisation"
                  ) : isLogin ? (
                    "Se connecter"
                  ) : (
                    "Créer mon compte"
                  )}
                </Button>

                {showResend && !isForgotPassword && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={resending}
                    onClick={handleResendConfirmation}
                  >
                    {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Renvoyer l'e-mail de confirmation
                  </Button>
                )}
              </form>

              <div className="mt-3 text-center space-y-1">
                {!isForgotPassword && isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setEmail("");
                      setPassword("");
                      setRecaptchaToken(null);
                      recaptchaRef.current?.reset();
                    }}
                    className="text-sm text-primary hover:underline"
                    disabled={loading}
                  >
                    Mot de passe oublié ?
                  </button>
                )}
                {(isForgotPassword || !betaClosed) && <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    // Bêta pleine : on revient sur l'onglet Connexion, sans basculer vers l'inscription.
                    if (betaClosed) setBetaTab("login");
                    else setIsLogin(!isLogin);
                    setRecaptchaToken(null);
                    recaptchaRef.current?.reset();
                  }}
                  className="text-sm text-primary hover:underline"
                  disabled={loading}
                >
                  {isForgotPassword
                    ? "Retour à la connexion"
                    : isLogin
                    ? "Pas encore de compte ? Créer un compte"
                    : "Déjà un compte ? Se connecter"}
                </button>}
              </div>
    </>
  );

  const panel = (
    <Card className="tama-surface-card w-full max-w-md">

        <CardHeader className="space-y-0.5 pb-3">
          <CardTitle className="flex flex-col items-center gap-2 text-center text-2xl font-bold tracking-tight">
            {isResetting ? (
              "Nouveau mot de passe"
            ) : isForgotPassword ? (
              "Mot de passe oublié"
            ) : (
              <>
                <span>{isLogin ? "Connexion à" : "Rejoindre"}</span>
                <BrandLogo className="h-8 w-auto max-w-[9rem]" />
              </>
            )}
          </CardTitle>
          <CardDescription className="text-center text-sm">
            {isResetting
              ? "Définissez votre nouveau mot de passe sécurisé"
              : isForgotPassword
              ? "Entrez votre email pour recevoir un lien de réinitialisation"
              : isLogin
              ? "Connectez-vous pour découvrir du contenu de Polynésie française"
              : "Créez un compte pour partager vos vidéos"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 pb-5">
          {isResetting ? (
            <form onSubmit={handleUpdatePassword} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="newPassword" className="text-sm">Nouveau mot de passe <span className="text-destructive">*</span></Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder={`Minimum ${passwordMinLength} caractères`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmNewPassword" className="text-sm">Confirmer le mot de passe <span className="text-destructive">*</span></Label>
                <Input
                  id="confirmNewPassword"
                  type="password"
                  placeholder="Confirmez votre mot de passe"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Mise à jour...
                  </>
                ) : (
                  "Mettre à jour le mot de passe"
                )}
              </Button>
            </form>
          ) : (
            <>
              {!isForgotPassword && isLoadingSettings && (
                <>
                  <div className="space-y-2">
                    <Skeleton className="h-9 w-full" />
                  </div>
                  <div className="relative my-4">
                    <Separator />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                      ou
                    </span>
                  </div>
                </>
              )}

              {!isForgotPassword && !isLoadingSettings && showOAuthButtons && (
                <>
                  <div className="space-y-2">
                    {isGoogleOAuthEnabled && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-9"
                        onClick={() => handleOAuthSignIn('google')}
                        disabled={loading || oauthProvider !== null}
                      >
                        {oauthProvider === 'google' ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Continuer avec Google
                      </Button>
                    )}
                    {isAppleOAuthEnabled && isAppleDevice && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-9"
                        onClick={() => handleOAuthSignIn('apple')}
                        disabled={loading || oauthProvider !== null}
                      >
                        {oauthProvider === 'apple' ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M17.05 12.54c.02-2.15 1.75-3.18 1.83-3.23-1-1.46-2.56-1.66-3.11-1.68-1.32-.13-2.58.77-3.25.77-.68 0-1.71-.75-2.81-.73-1.45.02-2.78.84-3.53 2.13-1.5 2.6-.38 6.45 1.08 8.57.72 1.03 1.57 2.19 2.69 2.15 1.08-.04 1.49-.7 2.79-.7 1.3 0 1.67.7 2.8.68 1.16-.02 1.9-1.05 2.61-2.09.82-1.19 1.16-2.35 1.18-2.41-.03-.01-2.26-.87-2.28-3.46zM14.9 5.62c.59-.72.99-1.72.88-2.72-.85.04-1.89.57-2.5 1.28-.55.63-1.03 1.65-.9 2.62.95.08 1.92-.48 2.52-1.18z"/>
                          </svg>
                        )}
                        Continuer avec Apple
                      </Button>
                    )}
                  </div>

                  <div className="relative my-4">
                    <Separator />
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                      ou
                    </span>
                  </div>
                </>
              )}

              {showBetaTabs ? (
                <Tabs value={betaTab} onValueChange={(v) => setBetaTab(v as "waitlist" | "login")}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="waitlist">Liste d'attente</TabsTrigger>
                    <TabsTrigger value="login">Connexion</TabsTrigger>
                  </TabsList>
                  <TabsContent value="waitlist"><BetaWaitlistForm inTab /></TabsContent>
                  <TabsContent value="login" className="pt-2">{loginForm}</TabsContent>
                </Tabs>
              ) : loginForm}

              <div className="mt-4 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  En continuant, vous acceptez{" "}
                  <Link
                    to="/legal/terms"
                    className="underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                  >
                    les conditions d'utilisation de Tama
                  </Link>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
  );

  return embedded ? panel : (
    <main className="min-h-screen flex items-center justify-center bg-background p-4">{panel}</main>
  );
};

export default AuthPanel;

